// ============================================================================
// InfiniteScrollPagination.jsx
// Auto-loads more products when scrolling to bottom
// ============================================================================

import React, {useEffect, useRef, useCallback, useState} from 'react';
import {Skeleton} from '@material-ui/lab';
import './InfiniteScrollPagination.css';
import {Spinner} from "react-bootstrap";
import {SpinnerWrapper} from "../../../containers/SalePromotionCampaign/SaleCampaign.style";

/**
 * Infinite Scroll Pagination Component
 * Automatically loads more items when user scrolls near bottom
 *
 * @param {number} currentPage - Current page number
 * @param {number} totalPages - Total number of pages
 * @param {number} totalItems - Total number of items
 * @param {number} itemsPerPage - Items per page
 * @param {function} onPageChange - Callback when page changes
 * @param {boolean} loading - Loading state
 * @param {boolean} hasMore - Whether there are more items to load
 */
export const InfiniteScrollPagination = ({
	                                         currentPage,
	                                         totalPages,
	                                         totalItems,
	                                         itemsPerPage,
	                                         onPageChange,
	                                         loading = false,
	                                         hasMore = true,
	                                         threshold = 600, // Distance from bottom to trigger load (px)
                                         }) => {
	const observerTarget = useRef(null);
	const [isLoadingMore, setIsLoadingMore] = useState(false);

	/**
	 * Check if there are more pages to load
	 */
	const canLoadMore = currentPage < totalPages && hasMore && !loading;

	/**
	 * Handle loading next page
	 */
	const loadMore = useCallback(() => {
		if (canLoadMore && !isLoadingMore) {
			setIsLoadingMore(true);
			onPageChange(currentPage + 1);
			const scrollPosition = window.pageYOffset || document.documentElement.scrollTop - 600;

			// Reset loading state after a delay
			setTimeout(() => {
				setIsLoadingMore(false);
			}, 300);
		}
	}, [currentPage, canLoadMore, isLoadingMore, onPageChange]);

	/**
	 * Intersection Observer for infinite scroll
	 */
	useEffect(() => {
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting) {
					loadMore();
				}
			},
			{
				threshold: 0.1,
				rootMargin: `${threshold}px`,
			}
		);

		const currentTarget = observerTarget.current;

		if (currentTarget) {
			observer.observe(currentTarget);
		}

		return () => {
			if (currentTarget) {
				observer.unobserve(currentTarget);
			}
		};
	}, [loadMore, threshold]);

	/**
	 * Alternative: Scroll event listener (backup method)
	 */
	useEffect(() => {
		const handleScroll = () => {
			if (loading || !canLoadMore || isLoadingMore) return;

			const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
			const scrollHeight = document.documentElement.scrollHeight;
			const clientHeight = window.innerHeight;

			// Trigger when user is near bottom
			if (scrollTop + clientHeight >= scrollHeight - threshold) {
				loadMore();
			}
		};

		// Throttle scroll event
		let timeoutId;
		const throttledScroll = () => {
			if (timeoutId) return;
			timeoutId = setTimeout(() => {
				handleScroll();
				timeoutId = null;
			}, 100);
		};

		window.addEventListener('scroll', throttledScroll);

		return () => {
			window.removeEventListener('scroll', throttledScroll);
			if (timeoutId) clearTimeout(timeoutId);
		};
	}, [loading, canLoadMore, isLoadingMore, loadMore, threshold]);

	// Don't render if only 1 page
	if (totalPages <= 1) return null;

	return (
		<div className="infinite-scroll-pagination">
			{/* Loading indicator */}
			{(loading || isLoadingMore) && (
				<div className={"d-flex justify-content-center"}>
					<Spinner animation="grow" size="sm" style={{color: "#9cc54b"}}/>
					<Spinner animation="grow" size="sm" style={{color: "#9cc54b"}}/>
					<Spinner animation="grow" size="sm" style={{color: "#9cc54b"}}/>
				</div>)}
		</div>
	);
};

export default InfiniteScrollPagination;